import React from "react";

/**
 * How strong a password is, and why.
 *
 * Deliberately not a hard gate: the server's rule is six characters, and a
 * meter that refuses to let a parent continue would only push them to write
 * the password on the fridge. It scores what is there and says the one thing
 * that would help most — length first, because length is what actually helps.
 */

export type PasswordVerdict = {
  /** 0 (empty) to 4 (strong). */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** The single most useful next step, or null when there is nothing to add. */
  hint: string | null;
};

const COMMON = [
  "parola",
  "password",
  "123456",
  "12345678",
  "qwerty",
  "qwertyuiop",
  "111111",
  "abc123",
  "iubire",
  "fotbal",
  "admin",
  "welcome",
];

export function scorePassword(raw: string): PasswordVerdict {
  const pw = String(raw || "");
  if (!pw) return { score: 0, label: "", hint: null };

  const lower = pw.toLowerCase();
  // A password that only dresses up a common word is weak however long it is.
  if (COMMON.some((c) => lower.includes(c))) {
    return { score: 1, label: "Prea ușor de ghicit", hint: "Evită cuvintele comune, ca „parola” sau „123456”." };
  }

  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  // Length carries most of the weight; variety is a bonus on top of it.
  let points = 0;
  if (pw.length >= 6) points += 1;
  if (pw.length >= 10) points += 1;
  if (pw.length >= 14) points += 1;
  if (classes >= 2) points += 1;
  if (classes >= 3) points += 1;

  const score = (Math.min(4, Math.max(1, points)) as 1 | 2 | 3 | 4);

  const hint =
    pw.length < 6
      ? "Minim 6 caractere."
      : pw.length < 10
      ? "Mai lungă e mai sigură — încearcă 10 caractere."
      : classes < 2
      ? "Adaugă o cifră sau o literă mare."
      : null;

  const label = ["", "Slabă", "Acceptabilă", "Bună", "Puternică"][score];
  return { score, label, hint };
}

const BAR = ["bg-slate-200", "bg-rose-500", "bg-amber-500", "bg-lime-500", "bg-emerald-500"];
const TEXT = ["text-slate-400", "text-rose-600", "text-amber-600", "text-lime-600", "text-emerald-600"];

export default function PasswordMeter({ value, id }: { value: string; id?: string }) {
  const { score, label, hint } = scorePassword(value);
  if (!value) return null;

  return (
    <div className="mt-2" id={id}>
      <div className="flex gap-1" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? BAR[score] : "bg-slate-200"}`}
          />
        ))}
      </div>
      <p className={`mt-1 text-xs font-medium ${TEXT[score]}`} aria-live="polite">
        {label}
        {hint && <span className="font-normal text-slate-400"> · {hint}</span>}
      </p>
    </div>
  );
}
